import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Flex, Text, Heading, Divider, Loader, Counter } from '@vibe/core';
import { Home, Time, Calendar, Settings, Chart, Board, Person, NavigationDoubleChevronLeft } from '@vibe/icons';
import { useMondayContext } from '../context/MondayContext';
import { useCurrentUserAccess } from '../hooks/useCurrentUserAccess';

// Report components
import ReportsGallery from '../components/reports/ReportsGallery';
import ReportConfigDialog from '../components/reports/ReportConfigDialog';
import UtilizationReportTable from '../components/reports/UtilizationReportTable';
import UtilizationReportHeader from '../components/reports/UtilizationReportHeader';
import BoardResourcesTable from '../components/reports/BoardResourcesTable';
import BoardResourcesHeader from '../components/reports/BoardResourcesHeader';
import LeaveReportTable from '../components/reports/LeaveReportTable';
import LeaveReportHeader from '../components/reports/LeaveReportHeader';
import WorklogReportTable from '../components/reports/WorklogReportTable';
import WorklogReportHeader from '../components/reports/WorklogReportHeader';
import PlannedVsActualHeader from '../components/reports/PlannedVsActualHeader';
import PlannedVsActualItemTable from '../components/reports/PlannedVsActualItemTable';
import PlannedVsActualBoardTable from '../components/reports/PlannedVsActualBoardTable';
import { useReportStore } from '../stores/reportStore';
import type { UtilizationReportResponse, BoardResourcesReportResponse, LeaveReportResponse, WorklogReportResponse, PlannedVsActualReportResponse } from '../api/report.api';

// Leave components
import MyLeavesTab from '../components/leave/MyLeavesTab';
import ApprovalsTab from '../components/leave/ApprovalsTab';
import LeaveManageTab from '../components/leave/LeaveManageTab';
import LeaveRequestDialog from '../components/leave/LeaveRequestDialog';
import { getLeaveRequestsPendingCount } from '../api/leave.api';

// Time tracking components
import TimeTrackingWorkspace from '../components/timetracking/TimeTrackingWorkspace';

// Timesheet components
import TimesheetWorkspace from '../components/timesheet/TimesheetWorkspace';

// Workspace Scheduler
import WorkspaceScheduler from '../components/workspace/WorkspaceScheduler';

// Settings components
import AppSettingsPanel from '../components/settings/AppSettings';
import MyAccountPanel from '../components/settings/MySettings';

// Styles
import '../styles/leave.css';
import '../styles/recurring.css';
import '../styles/timetracking.css';
import '../styles/workspace-scheduler.css';
import '../styles/reports.css';

// ─── Sidebar nav items ───────────────────────────────────────────────────────

interface NavItem {
  key: string;
  label: string;
  icon: React.FC<Record<string, unknown>>;
  path: string;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { key: 'scheduler', label: 'Scheduler', icon: Home, path: '/workspace/scheduler' },
  { key: 'timetracking', label: 'Time Tracking', icon: Time, path: '/workspace/timetracking' },
  { key: 'timesheets', label: 'Timesheets', icon: Board, path: '/workspace/timesheets' },
  { key: 'leave', label: 'Leave', icon: Calendar, path: '/workspace/leave' },
  { key: 'reports', label: 'Reports', icon: Chart, path: '/workspace/reports' },
  { key: 'settings', label: 'Settings', icon: Settings, path: '/workspace/settings', adminOnly: true },
  { key: 'my-account', label: 'My Account', icon: Person, path: '/workspace/my-account' },
];

const MODULES_EXCLUDED_FROM_STORAGE = new Set(['settings', 'my-account']);
const LS_LAST_MODULE_KEY = 'rp_workspace_last_module';

// ─── Component ────────────────────────────────────────────────────────────────

const WorkspaceView: React.FC = () => {
  const { context, isReady } = useMondayContext();
  const { isAdmin } = useCurrentUserAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('rp_workspace_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('rp_workspace_sidebar_collapsed', String(next)); } catch { /* noop */ }
      return next;
    });
    setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
  }, []);

  // Restore last module on initial mount (if on welcome page)
  useEffect(() => {
    if (location.pathname === '/workspace' || location.pathname === '/workspace/') {
      try {
        const saved = localStorage.getItem(LS_LAST_MODULE_KEY);
        if (saved) {
          const item = NAV_ITEMS.find((n) => n.key === saved);
          if (item && (!item.adminOnly || isAdmin)) {
            navigate(item.path, { replace: true });
          }
        }
      } catch { /* noop */ }
    }
    // Run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save active module to localStorage when it changes
  useEffect(() => {
    const path = location.pathname;
    let key = '';
    if (path.startsWith('/workspace/scheduler')) key = 'scheduler';
    else if (path.startsWith('/workspace/timetracking')) key = 'timetracking';
    else if (path.startsWith('/workspace/timesheets')) key = 'timesheets';
    else if (path.startsWith('/workspace/leave')) key = 'leave';
    else if (path.startsWith('/workspace/reports')) key = 'reports';
    // Don't save settings or my-account
    if (key && !MODULES_EXCLUDED_FROM_STORAGE.has(key)) {
      try { localStorage.setItem(LS_LAST_MODULE_KEY, key); } catch { /* noop */ }
    }
  }, [location.pathname]);

  if (!isReady) {
    return (
      <div className="rp-view-loading">
        <Loader size={Loader.sizes.MEDIUM} />
      </div>
    );
  }

  const getActiveKey = (): string => {
    const path = location.pathname;
    if (path.startsWith('/workspace/scheduler')) return 'scheduler';
    if (path.startsWith('/workspace/timetracking')) return 'timetracking';
    if (path.startsWith('/workspace/timesheets')) return 'timesheets';
    if (path.startsWith('/workspace/leave')) return 'leave';
    if (path.startsWith('/workspace/reports')) return 'reports';
    if (path.startsWith('/workspace/settings')) return 'settings';
    if (path.startsWith('/workspace/my-account')) return 'my-account';
    return '';
  };

  const activeKey = getActiveKey();
  const isOnWelcome = activeKey === '';

  return (
    <div className="rp-workspace-view">
      {/* Sidebar – only visible when a module is selected */}
      {!isOnWelcome && (
        <nav className={`rp-workspace-view__sidebar ${sidebarCollapsed ? 'rp-workspace-view__sidebar--collapsed' : ''}`}>
          <div className="rp-workspace-view__sidebar-header">
            <button
              className="rp-workspace-view__sidebar-toggle"
              onClick={toggleSidebar}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <NavigationDoubleChevronLeft style={sidebarCollapsed ? { transform: 'rotate(180deg)' } : undefined} />
            </button>
          </div>
          <div className="rp-workspace-view__sidebar-nav">
            {NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin).map((item) => {
              const Icon = item.icon;
              const isActive = activeKey === item.key;
              return (
                <button
                  key={item.key}
                  className={`rp-workspace-view__nav-item ${
                    isActive ? 'rp-workspace-view__nav-item--active' : ''
                  }`}
                  onClick={() => navigate(item.path)}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* Main content area */}
      <div className="rp-workspace-view__content">
        <Routes>
          <Route index element={<WelcomePanel navigate={navigate} isAdmin={isAdmin} />} />
          <Route path="scheduler" element={<WorkspaceScheduler />} />
          <Route path="timetracking" element={<TimeTrackingWorkspace currentUserId={context.userId} isAdmin={isAdmin} />} />
          <Route path="timesheets" element={<TimesheetWorkspace />} />
          <Route path="leave" element={<LeavePanel currentUserId={context.userId} isAdmin={isAdmin} />} />

          <Route path="reports" element={<ReportsPanel />} />
          <Route path="settings" element={<SettingsPanel />} />
          <Route path="my-account" element={<MyAccountPanel />} />
        </Routes>
      </div>
    </div>
  );
};

// ─── Panel Components ─────────────────────────────────────────────────────────

/**
 * Welcome screen shown when user first opens the workspace.
 * Displays module cards so the user can choose which module to open.
 */
const WelcomePanel: React.FC<{
  navigate: (path: string) => void;
  isAdmin: boolean;
}> = ({ navigate, isAdmin }) => {
  const modules = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  return (
    <div className="rp-welcome-panel">
      <div className="rp-welcome-panel__header">
        <Heading type={Heading.types.H1} weight={Heading.weights.BOLD}>
          Resource Planning & Time Tracking
        </Heading>
        <Text type={Text.types.TEXT1} style={{ color: 'var(--secondary-text-color)', marginTop: 4 }}>
          Select a module to get started
        </Text>
      </div>
      <div className="rp-welcome-panel__grid">
        {modules.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              className="rp-welcome-panel__card"
              onClick={() => navigate(item.path)}
            >
              <div className="rp-welcome-panel__card-icon">
                <Icon />
              </div>
              <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
                {item.label}
              </Text>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Leave panel with sub-navigation for requests, calendar, and admin.
 */
const LeavePanel: React.FC<{ currentUserId: number | null; isAdmin?: boolean }> = ({
  currentUserId,
  isAdmin,
}) => {
  type LeaveSubView = 'my_leaves' | 'approvals' | 'manage';
  const [subView, setSubView] = useState<LeaveSubView>('my_leaves');
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    getLeaveRequestsPendingCount()
      .then(setPendingCount)
      .catch(() => {});
  }, []);

  const navItems: {
    key: LeaveSubView;
    label: string;
    badgeCount?: number;
    adminOnly?: boolean;
  }[] = [
    { key: 'my_leaves', label: 'My Leaves' },
    { key: 'approvals', label: 'Approvals', badgeCount: pendingCount },
    { key: 'manage', label: 'Manage', adminOnly: true },
  ];

  return (
    <div className="rp-leave-panel">
      {/* Sub-navigation */}
      <Flex gap={Flex.gaps.SMALL} className="rp-leave-panel__sub-nav">
        {navItems.filter((item) => !item.adminOnly || isAdmin).map((item) => (
          <button
            key={item.key}
            className={`rp-leave-panel__sub-nav-btn ${
              subView === item.key
                ? 'rp-leave-panel__sub-nav-btn--active'
                : ''
            }`}
            onClick={() => setSubView(item.key)}
          >
            {item.label}
            {(item.badgeCount ?? 0) > 0 && (
              <Counter
                count={item.badgeCount!}
                size={Counter.sizes.SMALL}
                color={Counter.colors.PRIMARY}
              />
            )}
          </button>
        ))}
      </Flex>

      {/* Sub-view content */}
      {subView === 'my_leaves' && (
        <MyLeavesTab
          currentUserId={currentUserId}
          onCreateNew={() => setShowLeaveDialog(true)}
          refreshKey={refreshKey}
        />
      )}
      {subView === 'approvals' && (
        <ApprovalsTab
          currentUserId={currentUserId}
          onPendingCountChange={setPendingCount}
        />
      )}
      {subView === 'manage' && (
        <LeaveManageTab currentUserId={currentUserId} />
      )}

      {/* Leave Request Dialog (modal) */}
      {showLeaveDialog && currentUserId && (
        <LeaveRequestDialog
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          onSubmitted={() => {
            setShowLeaveDialog(false);
            setRefreshKey((k) => k + 1);
          }}
          onClose={() => setShowLeaveDialog(false)}
        />
      )}
    </div>
  );
};

/**
 * Settings panel – renders AppSettings directly (which has its own internal nav).
 */
const SettingsPanel: React.FC = () => {
  return <AppSettingsPanel />;
};

/**
 * Reports panel that manages gallery → config → table flow via store state.
 */
const PlannedVsActualContainer: React.FC<{ data: PlannedVsActualReportResponse }> = ({ data }) => {
  const { currentConfig } = useReportStore();
  const initialView = currentConfig?.groupBy === 'board' ? 'board' : 'item';
  const [activeView, setActiveView] = React.useState<'item' | 'board'>(initialView);
  return (
    <>
      <PlannedVsActualHeader data={data} activeView={activeView} onViewChange={setActiveView} />
      {activeView === 'item' ? (
        <PlannedVsActualItemTable data={data} />
      ) : (
        <PlannedVsActualBoardTable data={data} />
      )}
    </>
  );
};

const ReportsPanel: React.FC = () => {
  const { view, reportData, reportLoading, reportError } = useReportStore();

  if (view === 'config') {
    return <ReportConfigDialog />;
  }

  if (view === 'table') {
    if (reportLoading) {
      return (
        <div className="rp-report-loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      );
    }
    if (reportError) {
      return <div className="rp-report-error">{reportError}</div>;
    }
    if (reportData) {
      if (reportData.reportType === 'planned_vs_actual') {
        return <PlannedVsActualContainer data={reportData as PlannedVsActualReportResponse} />;
      }
      if (reportData.reportType === 'board_resources_forecast') {
        const brData = reportData as BoardResourcesReportResponse;
        return (
          <>
            <BoardResourcesHeader data={brData} />
            <BoardResourcesTable data={brData} />
          </>
        );
      }
      if (reportData.reportType === 'leave_report') {
        const leaveData = reportData as LeaveReportResponse;
        return (
          <>
            <LeaveReportHeader data={leaveData} />
            <LeaveReportTable data={leaveData} />
          </>
        );
      }
      if (reportData.reportType === 'detailed_worklog') {
        const wlData = reportData as WorklogReportResponse;
        return (
          <>
            <WorklogReportHeader data={wlData} />
            <WorklogReportTable data={wlData} />
          </>
        );
      }
      // Default: utilization forecast
      const utilData = reportData as UtilizationReportResponse;
      return (
        <>
          <UtilizationReportHeader data={utilData} />
          <UtilizationReportTable data={utilData} />
        </>
      );
    }
  }

  return <ReportsGallery />;
};

export default WorkspaceView;
